<div class="dialog-wrapper">
  <app-modal-header-common [title]="dialogTitle" [dialogRef]="dialogRef">
  </app-modal-header-common>

  <div class="dialog-content">
    <app-loading *ngIf="loader"></app-loading>

    <div class="risk-form" *ngIf="!loader">
      <form [formGroup]="riskForm">

        <!-- Breadcrumbs -->
        <div style="font-size: 14px; color: #454545; margin-bottom: 15px;">
          <span style="margin-left: 2px;">{{ domainName }}</span>
          <span style="margin: 0 8px;">&gt;</span>
          <span style="font-weight: 500;">{{ subdomainName }}</span>
        </div>

        <!-- SECTION 1: BASIC DETAILS -->
        <div class="form-section mb-4">
          <div class="section-title-bar">
            <span>Basic Details</span>
          </div>

          <!-- Row 1: Risk ID, Type, Risk Name & Date Identified -->
          <div class="row g-3 mb-2">
            <div class="col-md-2">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Risk ID <span class="required-asterisk">*</span></mat-label>
                <input matInput formControlName="riskId" />
              </mat-form-field>
            </div>
            <div class="col-md-2">
              <app-searchable-dropdown class="w-100" labelText="Type" formControlName="type" [options]="typeList"
                [valueKey]="'id'" [labelKey]="'display_name'" [disabled]="isReadOnly">
              </app-searchable-dropdown>
            </div>
            <div class="col-md-5">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Risk Name <span class="required-asterisk">*</span></mat-label>
                <input matInput formControlName="name" />
              </mat-form-field>
            </div>
            <div class="col-md-3">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Date Identified</mat-label>
                <input matInput [matDatepicker]="dateIdentifiedPicker" formControlName="date_identified" [disabled]="isReadOnly" />
                <mat-datepicker-toggle matSuffix [for]="dateIdentifiedPicker" [disabled]="isReadOnly"></mat-datepicker-toggle>
                <mat-datepicker #dateIdentifiedPicker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>

          <!-- Row 2: Description -->
          <div class="row g-3 mb-2">
            <div class="col-12">
              <mat-form-field class="w-100" appearance="outline">
                <mat-label>Description <span class="required-asterisk">*</span></mat-label>
                <textarea matInput formControlName="description" rows="3"></textarea>
              </mat-form-field>
            </div>
          </div>

          <!-- Row 3: Vulnerabilities -->
          <div class="row g-3">
            <div class="col-12">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="fw-bold text-secondary" style="font-size: 13px;">Vulnerability / Root Cause of Risk</div>
                <button *ngIf="!isReadOnly" type="button" mat-button color="primary" (click)="addVulnerabilityRow()"
                  style="font-size: 12px; font-weight: 500;">
                  <mat-icon
                    style="font-size: 16px; width: 16px; height: 16px; line-height: 16px; vertical-align: middle; margin-right: 2px;">add</mat-icon>
                  Add Vulnerability
                </button>
              </div>
              <div class="table-container table-responsive control-table vulnerability-table-container">
                <table class="table table-bordered mb-0" *ngIf="vulnerabilitiesArray.controls.length > 0">
                  <thead style="position: sticky; z-index: 1; top: 0; background: #f8fafc">
                    <tr>
                      <th class="text-center" style="width: 40px;">#</th>
                      <th class="text-center">Vulnerability / Root Cause Description</th>
                      <th *ngIf="!isReadOnly" class="text-center" style="width: 90px;">Action</th>
                    </tr>
                  </thead>

                  <tbody formArrayName="vulnerabilities">
                    <tr *ngFor="let vul of vulnerabilitiesArray.controls; let i = index;" [formGroupName]="i"
                      [ngStyle]="{ 'opacity': vul.get('is_ignored')?.value ? '0.5' : '1', 'background-color': vul.get('is_ignored')?.value ? '#f1f5f9' : 'transparent' }">
                      <td class="text-center align-middle fw-bold text-muted">{{ i + 1 }}</td>

                      <td class="pt-2">
                        <mat-form-field appearance="outline" style="width: 100%">
                          <mat-label>Vulnerability</mat-label>
                          <textarea matInput formControlName="vulnerability" cdkTextareaAutosize cdkAutosizeMinRows="1"
                            cdkAutosizeMaxRows="4"
                            [ngStyle]="{ 'text-decoration': vul.get('is_ignored')?.value ? 'line-through' : 'none' }"></textarea>
                        </mat-form-field>
                      </td>

                      <td *ngIf="!isReadOnly" class="text-center align-middle">
                        <button type="button" mat-icon-button
                          [color]="vul.get('is_ignored')?.value ? 'warn' : 'primary'"
                          (click)="toggleIgnoreVulnerability(i)"
                          [matTooltip]="vul.get('is_ignored')?.value ? 'Unignore Vulnerability' : 'Ignore Vulnerability'">
                          <mat-icon>{{ vul.get('is_ignored')?.value ? 'visibility_off' : 'visibility' }}</mat-icon>
                        </button>

                        <button type="button" mat-icon-button color="warn" (click)="removeVulnerabilityRow(i)"
                          matTooltip="Remove Vulnerability">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div *ngIf="isReadOnly && vulnerabilitiesArray.controls.length === 0" class="text-muted p-2 text-center"
                  style="font-size: 13px;">
                  No vulnerabilities recorded
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 2: CONTROLS -->
        <div class="form-section mb-4">
          <div class="section-title-bar">
            <span>Risk Controls</span>
          </div>

          <div class="row g-3">
            <!-- Preventive Controls -->
            <div class="col-md-6">
              <div class="fw-bold mb-2 text-secondary" style="font-size: 13px;">Preventive Controls</div>
              <div class="table-container table-responsive control-table">
                <table class="table table-bordered mb-0" *ngIf="preventiveControlsArray.controls.length > 0">
                  <thead style="position: sticky; z-index: 1; top: 0; background: #f8fafc">
                    <tr>
                      <th class="text-center" style="width: 40px;">#</th>
                      <th class="text-center">Preventive Control</th>
                      <th *ngIf="!isReadOnly" class="text-center" style="width: 80px;">Action</th>
                    </tr>
                  </thead>

                  <tbody formArrayName="preventiveControls">
                    <tr *ngFor="let ctrl of preventiveControlsArray.controls; let i = index; let isLast = last"
                      [formGroupName]="i">
                      <td class="text-center align-middle fw-bold text-muted">{{ i + 1 }}</td>

                      <td class="pt-2">
                        <mat-form-field appearance="outline" style="width: 100%">
                          <mat-label>Preventive Control</mat-label>
                          <textarea matInput formControlName="description" cdkTextareaAutosize cdkAutosizeMinRows="1"
                            cdkAutosizeMaxRows="4"></textarea>
                        </mat-form-field>
                      </td>

                      <td *ngIf="!isReadOnly" class="text-center align-middle">
                        <button *ngIf="isLast" type="button" mat-icon-button color="primary"
                          (click)="addPreventiveControlRow()" matTooltip="Add Preventive Control">
                          <mat-icon>add</mat-icon>
                        </button>

                        <button type="button" mat-icon-button color="warn" (click)="removePreventiveControlRow(i)"
                          matTooltip="Remove Control">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div *ngIf="isReadOnly && preventiveControlsArray.controls.length === 0"
                  class="text-muted p-2 text-center" style="font-size: 13px;">
                  No preventive controls recorded
                </div>
              </div>
            </div>

            <!-- Detective Controls -->
            <div class="col-md-6">
              <div class="fw-bold mb-2 text-secondary" style="font-size: 13px;">Detective Controls</div>
              <div class="table-container table-responsive control-table">
                <table class="table table-bordered mb-0" *ngIf="detectiveControlsArray.controls.length > 0">
                  <thead style="position: sticky; z-index: 1; top: 0; background: #f8fafc">
                    <tr>
                      <th class="text-center" style="width: 40px;">#</th>
                      <th class="text-center">Detective Control</th>
                      <th *ngIf="!isReadOnly" class="text-center" style="width: 80px;">Action</th>
                    </tr>
                  </thead>

                  <tbody formArrayName="detectiveControls">
                    <tr *ngFor="let ctrl of detectiveControlsArray.controls; let i = index; let isLast = last"
                      [formGroupName]="i">
                      <td class="text-center align-middle fw-bold text-muted">{{ i + 1 }}</td>

                      <td class="pt-2">
                        <mat-form-field appearance="outline" style="width: 100%">
                          <mat-label>Detective Control</mat-label>
                          <textarea matInput formControlName="description" cdkTextareaAutosize cdkAutosizeMinRows="1"
                            cdkAutosizeMaxRows="4"></textarea>
                        </mat-form-field>
                      </td>

                      <td *ngIf="!isReadOnly" class="text-center align-middle">
                        <button *ngIf="isLast" type="button" mat-icon-button color="primary"
                          (click)="addDetectiveControlRow()" matTooltip="Add Detective Control">
                          <mat-icon>add</mat-icon>
                        </button>

                        <button type="button" mat-icon-button color="warn" (click)="removeDetectiveControlRow(i)"
                          matTooltip="Remove Control">
                          <mat-icon>delete</mat-icon>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div *ngIf="isReadOnly && detectiveControlsArray.controls.length === 0"
                  class="text-muted p-2 text-center" style="font-size: 13px;">
                  No detective controls recorded
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 3: RISK ASSESSMENT MATRIX -->
        <div class="form-section mb-4">
          <div class="section-title-bar">
            <span>Risk Score Assessment</span>
          </div>

          <div class="row g-3">
            <!-- INHERENT RISK BLOCK -->
            <div class="col-md-6">
              <div class="score-block-item h-100">
                <div class="score-block-header">
                  <span class="block-title">Inherent Risk</span>
                </div>
                <div class="score-block-fields">
                  <div class="score-input-field">
                    <label class="field-label">Impact</label>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-select formControlName="inherent_risk_impact" (selectionChange)="onInherentImpactChange()">
                        <mat-option *ngFor="let item of riskImpactList" [value]="item.id">
                          {{ item.display_name }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <div class="score-input-field">
                    <label class="field-label">Likelihood</label>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-select formControlName="inherent_risk_likelihood"
                        (selectionChange)="onInherentLikelihoodChange()">
                        <mat-option *ngFor="let item of riskLikelihoodList" [value]="item.id">
                          {{ item.display_name }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <div class="score-badge-wrap">
                    <label class="field-label text-center">Score</label>
                    <div class="score-box"
                      [ngStyle]="{ 'background-color': getInherentRiskScoreColor(), color: 'white' }">
                      {{ inherentRiskScore ?? '0' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RESIDUAL RISK BLOCK -->
            <div class="col-md-6">
              <div class="score-block-item h-100">
                <div class="score-block-header">
                  <span class="block-title">Residual Risk</span>
                </div>
                <div class="score-block-fields">
                  <div class="score-input-field">
                    <label class="field-label">Impact</label>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-select formControlName="residual_risk_impact" (selectionChange)="onResidualImpactChange()">
                        <mat-option *ngFor="let item of riskImpactList" [value]="item.id">
                          {{ item.display_name }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <div class="score-input-field">
                    <label class="field-label">Likelihood</label>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-select formControlName="residual_risk_likelihood"
                        (selectionChange)="onResidualLikelihoodChange()">
                        <mat-option *ngFor="let item of riskLikelihoodList" [value]="item.id">
                          {{ item.display_name }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <div class="score-badge-wrap">
                    <label class="field-label text-center">Score</label>
                    <div class="score-box"
                      [ngStyle]="{ 'background-color': getResidualRiskScoreColor(), color: 'white' }">
                      {{ residualRiskScore ?? '0' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- SECTION 4: MITIGATION TASKS -->
        <div class="form-section mb-4">
          <div class="section-title-bar">
            <span>Mitigation Tasks</span>
          </div>

          <div class="table-container table-responsive control-table">
            <table class="table table-bordered mb-0" *ngIf="mitigationsArray.controls.length > 0">
              <thead style="position: sticky; z-index: 1; top: 0; background: #f8fafc">
                <tr>
                  <th class="text-center" style="width: 50px;">#</th>
                  <th class="text-center">Task Description</th>
                  <th *ngIf="!isReadOnly" class="text-center" style="width: 90px;">Action</th>
                </tr>
              </thead>

              <tbody formArrayName="mitigations">
                <tr *ngFor="let mit of mitigationsArray.controls; let i = index; let isLast = last" [formGroupName]="i">
                  <td class="text-center align-middle fw-bold text-muted">{{ i + 1 }}</td>

                  <td class="pt-2">
                    <mat-form-field appearance="outline" style="width: 100%">
                      <mat-label>Task</mat-label>
                      <textarea matInput formControlName="task" cdkTextareaAutosize cdkAutosizeMinRows="1"
                        cdkAutosizeMaxRows="4"></textarea>
                    </mat-form-field>
                  </td>

                  <td *ngIf="!isReadOnly" class="text-center align-middle">
                    <button *ngIf="isLast" type="button" mat-icon-button color="primary" (click)="addMitigationRow()"
                      matTooltip="Add Task">
                      <mat-icon>add</mat-icon>
                    </button>

                    <button type="button" mat-icon-button color="warn" (click)="removeMitigationRow(i)"
                      matTooltip="Remove Task">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div *ngIf="isReadOnly && mitigationsArray.controls.length === 0" class="text-muted p-2 text-center"
              style="font-size: 13px;">
              No mitigation tasks recorded
            </div>
          </div>
        </div>

        <!-- SECTION 5: ASSOCIATED RISKS -->
        <div class="form-section mb-3">
          <div class="section-title-bar">
            <span>Associated Risks</span>
          </div>
          <p class="text-muted small mb-2">Select all relevant risks from the catalog</p>

          <mat-form-field class="w-100" appearance="outline">
            <mat-chip-list #riskChipList aria-label="Associated Risks selection">
              <mat-chip *ngFor="let risk of selectedRisks; let i = index" [selectable]="!isReadOnly"
                [removable]="!isReadOnly" (removed)="removeRisk(i)">
                {{ displayRisk(risk) }}
                <mat-icon *ngIf="!isReadOnly" matChipRemove>cancel</mat-icon>
              </mat-chip>

              <input *ngIf="!isReadOnly" type="text" placeholder="Search risks..." [formControl]="riskInputControl"
                [matChipInputFor]="riskChipList" [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                [matChipInputAddOnBlur]="false" [matAutocomplete]="riskAuto" #riskAutoTrigger="matAutocompleteTrigger"
                (click)="riskAutoTrigger.openPanel()" />
            </mat-chip-list>

            <span *ngIf="isReadOnly && (!selectedRisks || selectedRisks.length === 0)" class="text-muted small ps-1">
              No associated risks
            </span>

            <mat-autocomplete *ngIf="!isReadOnly" #riskAuto="matAutocomplete" [displayWith]="displayRisk"
              (optionSelected)="addRisk($event.option.value)">
              <mat-option *ngFor="let option of riskFilteredOptions | async" [value]="option">
                <b>{{ displayRisk(option) }}</b>
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
        </div>

      </form>
    </div>
  </div>

  <!-- DIALOG FOOTER -->
  <div class="dialog-footer-actions">
    <ng-container *ngIf="isReadOnly">
      <button mat-flat-button mat-button class="mx-1" (click)="closeModal()">
        Close
      </button>
    </ng-container>
    <ng-container *ngIf="!isReadOnly">
      <button mat-button class="mx-1" (click)="closeModal()">
        Cancel
      </button>
      <button mat-flat-button color="primary" class="mx-1" [disabled]="riskForm.invalid" (click)="save()">
        {{ isEditMode ? "Update Risk" : "Save Risk" }}
      </button>
    </ng-container>
  </div>
</div>